<script lang="ts">
	import { createEventDispatcher, onMount, afterUpdate, onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import { Stack } from '@Shelly/shelly-design-system';
	import type { ActionItem, FlowNode } from '../../types/flow-types';
	import BaseInspector from '../BaseInspector.svelte';
	import InlineDeviceSelector from '../Shared/InlineDeviceSelector.svelte';
	import ChannelSelector from '../Shared/ChannelSelector.svelte';
	import DelayField from '../Shared/DelayField.svelte';
	import { devices } from '../../../../stores';
	import {
		readDeviceSettings,
		readDeviceStatus,
	} from '../../../../stores/device-runtime/status-selectors';
	import { get } from 'svelte/store';
	import { generateActionLabel } from '../helpers/LabelGenerator';
	import { real_id } from '../../../../helpers/interface_helpers';
	import { resolveDeviceInStore } from '../../helpers/resolveDeviceInStore';
	import { _init_action_components_v2 } from '../../../Scenes/SceneWizard/Process/Do/Device/helpers';
	import { _deepClone, _deepCopyArr } from '../../../../helpers/utilities';
	import { deviceActionInterpretator } from '../../../Scenes/helpers/scene_actions_to_ui';

	import ChangeStateStep from '../../../Scenes/SceneWizard/Process/Do/АddEditActionWizard/Steps/DeviceAction/ChangeStateStep.svelte';

	export let node: FlowNode;

	const dispatch = createEventDispatcher<{
		update: FlowNode;
	}>();

	// Track which node ID we've initialized for, so we don't re-init on our own updates
	let initializedForNodeId = '';
	let hasMounted = false;

	// When the devices store hydrates after first paint, rebuild sceneData (components, selectedDevice card).
	let deferredDeviceId: string | null = null;

	/** JSON fingerprint so afterUpdate catches in-place edits to deviceAction.actions, not only [] truthiness. */
	let lastActionsSyncFingerprint = '';

	/**
	 * TimerV2 mutates `sceneData.deviceAction.timer` in place, so ActionInspector often does not
	 * re-run `afterUpdate`. A light poll copies timer onto the flow node for canvas preview only
	 * (see syncActionsToNode for full wizard → node sync). Delay uses DelayField on this panel and
	 * is not read from sceneData here to avoid overwriting `node.data.delay`.
	 */
	let wizardTimerPollHandle: ReturnType<typeof setInterval> | undefined;

	/**
	 * Normalize timer to a comparable fingerprint: treat value ≤ 0 / absent as equivalent to
	 * "no timer", so {value:0, units:'sec'} and undefined both produce the sentinel "none".
	 * This prevents spurious poll dispatches caused by the wizard always carrying a zero-value
	 * timer object while node.data.timer is undefined (already deleted).
	 */
	const timerFingerprint = (t: { value?: number; units?: string } | null | undefined): string => {
		const v = t != null ? Number(t.value) : 0;
		return v > 0 ? `${v}:${t?.units ?? 'sec'}` : 'none';
	};

	const wizardTimerSyncFingerprint = (): string => timerFingerprint(sceneData?.deviceAction?.timer);
	const nodeTimerSyncFingerprint = (): string =>
		timerFingerprint(node.data.timer as { value?: number; units?: string } | undefined);

	const syncWizardTimerToFlowNode = () => {
		const resolvedDeviceId = sceneData?.selectedDevice?.id || sceneData?.deviceAction?.device?.id;
		if (!resolvedDeviceId || !node?.data) return;
		if (
			!Array.isArray(sceneData?.deviceAction?.components) ||
			sceneData.deviceAction.components.length === 0
		) {
			return;
		}

		if (wizardTimerSyncFingerprint() === nodeTimerSyncFingerprint()) return;

		const t = sceneData.deviceAction.timer;
		if (t && Number(t.value) > 0) {
			node.data.timer = _deepClone(t) as any;
		} else {
			delete node.data.timer;
		}

		if (!node.data.isLabelManuallyOverridden) {
			node.data.displayLabel = generateActionLabel(node);
		}
		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	};

	// --- sceneData bridge for ChangeStateStep ---
	let sceneData = buildSceneDataFromNode(node);
	initializedForNodeId = node.id;
	const initialDevId = node.data.device?.id as string | undefined;
	const initialChannelNum = parseInt(String(node.data.device?.channel ?? '0'), 10) || 0;
	deferredDeviceId =
		initialDevId && !resolveDeviceInStore(get(devices), initialDevId, initialChannelNum)
			? initialDevId
			: null;

	/**
	 * Builds the sceneData bridge object that ChangeStateStep and its children consume.
	 *
	 * Key invariants:
	 * - Actions are cloned with `_deepCopyArr` (not `_deepClone`) to preserve array identity —
	 *   `_deepClone` always returns a plain object, which causes ChangeStateStep to wipe the array.
	 * - `do_info.mode` is set to `'add'` when the node already has persisted actions, preventing
	 *   ChangeStateStep's edit-mode init block from clearing them for lights with a firmware alert.
	 * - Device lookup uses `resolveDeviceInStore` to handle suffixed store keys (e.g. `791803_0`).
	 * - Falls back to `deviceActionInterpretator` when `actions` is empty but raw `sceneData.r`
	 *   is present (e.g. nodes migrated from the old scene format before FlowToScene ran).
	 */
	function buildSceneDataFromNode(flowNode: FlowNode) {
		let deviceId = flowNode.data.device?.id || '';
		const channelRaw = flowNode.data.device?.channel;
		let channel = channelRaw !== undefined && channelRaw !== null ? String(channelRaw) : '0';
		let actions: any[] = Array.isArray(flowNode.data.actions)
			? (_deepCopyArr(flowNode.data.actions) as any[])
			: [];
		let delay: any = flowNode.data.delay
			? _deepClone(flowNode.data.delay)
			: { units: 'sec', value: 0 };
		let timer: any = flowNode.data.timer
			? _deepClone(flowNode.data.timer)
			: { units: 'sec', value: 0 };

		// Fallback: if actions are missing but raw scene JSON is available, reconstruct from it
		if (actions.length === 0 && flowNode.data.sceneData?.r) {
			try {
				const parsed = deviceActionInterpretator(flowNode.data.sceneData);
				if (parsed) {
					if (parsed.device?.id) {
						deviceId = parsed.device.id;
						channel = String(parsed.device.channel ?? '0');
					}
					if (Array.isArray(parsed.actions) && parsed.actions.length > 0) {
						actions = _deepCopyArr(parsed.actions) as any[];
					}
					if (parsed.delay) delay = _deepClone(parsed.delay);
					if (parsed.timer) timer = _deepClone(parsed.timer);

					flowNode.data.device = { id: deviceId, channel } as any;
					flowNode.data.actions = _deepCopyArr(actions) as any;
				}
			} catch (e) {
				console.warn('[ActionInspector] Failed to recover from sceneData:', e);
			}
		}

		const channelNum = parseInt(channel, 10);
		const channelForResolve = Number.isFinite(channelNum) ? channelNum : 0;

		// ChangeStateStep clears deviceAction.actions on init when do_info.mode === 'edit' and the
		// light descriptor has alert + empty addOns (e.g. firmware toggle notice). Use 'add' when
		// this node already has saved actions so the flow inspector keeps state/on/toggle selection.
		const doMode = !deviceId || actions.length > 0 ? 'add' : 'edit';

		const sd: any = {
			do_info: { mode: doMode, type: 'do', main_ind: -1 },
			selectedDevice: {},
			deviceAction: {
				device: { id: deviceId || undefined, channel },
				actions,
				delay,
				timer,
				components: [],
			},
			isValid: false,
			validStepIndex: 0,
			translated: undefined,
		};

		if (deviceId) {
			const devicesMap = get(devices);
			const device = resolveDeviceInStore(devicesMap, deviceId, channelForResolve);
			const displayName =
				(flowNode.data.device as { name?: string } | undefined)?.name || deviceId;

			if (device) {
				const statusKey = real_id(device.id ?? deviceId);
				sd.selectedDevice = {
					title: device.name || displayName,
					value: deviceId,
					id: deviceId,
					selected: true,
				};

				const status = readDeviceStatus(statusKey);
				const settings = readDeviceSettings(statusKey);

				try {
					sd.deviceAction.components = _init_action_components_v2(device, status, settings);
				} catch (e) {
					console.warn('[ActionInspector] Failed to init action components:', e);
					sd.deviceAction.components = [];
				}
			} else {
				sd.selectedDevice = {
					title: displayName,
					value: deviceId,
					id: deviceId,
					selected: true,
				};
			}
		}

		return sd;
	}

	onMount(() => {
		hasMounted = true;
		initializeFromNode();
		wizardTimerPollHandle = setInterval(() => {
			if (!hasMounted || !node || node.id !== initializedForNodeId) return;
			syncWizardTimerToFlowNode();
		}, 300);
	});

	/**
	 * Returns true when the node still carries raw scene JSON (`sceneData.r`, `turn`, `set`, …)
	 * that hasn't been migrated to the UI `actions` array yet. Used to prevent `syncActionsToNode`
	 * from flushing that fallback blob before the user has made an explicit action selection.
	 */
	function nodeHasSceneActionFallback(n: FlowNode): boolean {
		const sd = n.data.sceneData as Record<string, unknown> | undefined;
		if (!sd || typeof sd !== 'object') return false;
		return (
			typeof sd.r === 'string' ||
			sd.turn !== undefined ||
			sd.set !== undefined ||
			sd.notify !== undefined
		);
	}

	/**
	 * Produces a stable JSON fingerprint of the current wizard selection (device, channel, actions).
	 * `afterUpdate` and `onDestroy` use this to detect real changes and avoid redundant dispatches.
	 */
	function actionsSyncFingerprint(): string {
		try {
			const deviceId = sceneData?.selectedDevice?.id || sceneData?.deviceAction?.device?.id;
			const timer = sceneData?.deviceAction?.timer;
			const delay = sceneData?.deviceAction?.delay;
			return JSON.stringify({
				d: deviceId,
				ch: sceneData?.deviceAction?.device?.channel,
				a: sceneData?.deviceAction?.actions ?? [],
				t: timer ? { v: timer.value, u: timer.units } : null,
				y: delay ? { v: delay.value, u: delay.units } : null,
			});
		} catch {
			return '';
		}
	}

	afterUpdate(() => {
		if (!hasMounted || !node || node.id !== initializedForNodeId) return;
		const fp = actionsSyncFingerprint();
		if (!fp || fp === lastActionsSyncFingerprint) return;
		lastActionsSyncFingerprint = fp;
		syncActionsToNode();
	});

	onDestroy(() => {
		if (wizardTimerPollHandle) {
			clearInterval(wizardTimerPollHandle);
			wizardTimerPollHandle = undefined;
		}
		if (!hasMounted || !node || node.id !== initializedForNodeId) return;
		const fp = actionsSyncFingerprint();
		if (!fp || fp === lastActionsSyncFingerprint) return;
		lastActionsSyncFingerprint = fp;
		syncActionsToNode();
	});

	/**
	 * Writes the wizard's current selection back onto the flow node and dispatches an update.
	 * Guards prevent overwriting persisted actions with an empty in-progress wizard state, or
	 * stripping raw scene fallback data before the user has explicitly chosen an action.
	 */
	function syncActionsToNode() {
		const resolvedDeviceId = sceneData.selectedDevice?.id || sceneData.deviceAction?.device?.id;
		if (!resolvedDeviceId) return;

		const incoming = Array.isArray(sceneData.deviceAction.actions)
			? sceneData.deviceAction.actions
			: [];
		const existingLen = Array.isArray(node.data.actions) ? node.data.actions.length : 0;
		const hasSceneFallback = nodeHasSceneActionFallback(node);

		// Never replace persisted actions with an empty wizard list
		if (incoming.length === 0 && existingLen > 0) return;

		// Do not strip sceneData / flush empty actions before the user has chosen an action
		if (incoming.length === 0 && existingLen === 0 && hasSceneFallback) return;

		// Empty wizard, nothing on node to preserve — only sync device fields if they drifted
		if (incoming.length === 0) {
			const devId = String(resolvedDeviceId);
			const channel = String(sceneData.deviceAction.device.channel || '0');
			const dev = devId ? get(devices)[devId] : undefined;
			const sameDevice =
				node.data.device?.id === devId && String(node.data.device?.channel ?? '0') === channel;
			if (devId && !sameDevice) {
				node.data.device = {
					id: devId,
					name: dev?.name || devId,
					channel,
				};
				node = { ...node, data: { ...node.data } };
				dispatch('update', node);
			}
			return;
		}

		// Non-empty actions: commit full node payload and drop redundant scene blobs
		node.data.actions = _deepCopyArr(incoming) as ActionItem[];
		const devId = String(resolvedDeviceId);
		const channel = String(sceneData.deviceAction.device.channel || '0');
		const dev = devId ? get(devices)[devId] : undefined;
		node.data.device = {
			id: devId,
			name: dev?.name || devId,
			channel,
		};

		delete node.data.sceneData;
		delete node.data.originalAction;

		if (sceneData.deviceAction.delay?.value > 0) {
			node.data.delay = _deepClone(sceneData.deviceAction.delay) as {
				value: number;
				units: string;
			};
		} else {
			delete node.data.delay;
		}
		if (sceneData.deviceAction.timer?.value > 0) {
			node.data.timer = _deepClone(sceneData.deviceAction.timer) as any;
		} else {
			delete node.data.timer;
		}

		if (!node.data.isLabelManuallyOverridden) {
			node.data.displayLabel = generateActionLabel(node);
		}

		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	}

	// Local state
	let selectedDevice: string | null = null;
	let selectedChannel: number = 0;

	/**
	 * Reads the current node's device/channel/actions and rebuilds local UI state and sceneData.
	 * Called from `onMount` and whenever a different node is selected (reactive id guard).
	 */
	function initializeFromNode() {
		const deviceId = node.data.device?.id || null;
		selectedDevice = deviceId;
		selectedChannel = parseInt(String(node.data.device?.channel ?? '0'), 10) || 0;
		sceneData = buildSceneDataFromNode(node);
		deferredDeviceId =
			selectedDevice && !resolveDeviceInStore(get(devices), selectedDevice, selectedChannel)
				? selectedDevice
				: null;
	}

	// Re-initialize only when a different node is selected (not on our own store updates)
	$: if (node && node.id !== initializedForNodeId) {
		initializedForNodeId = node.id;
		lastActionsSyncFingerprint = '';
		initializeFromNode();
	}

	$: if (
		deferredDeviceId &&
		resolveDeviceInStore($devices, deferredDeviceId, selectedChannel) &&
		node?.id === initializedForNodeId
	) {
		lastActionsSyncFingerprint = '';
		sceneData = buildSceneDataFromNode(node);
		deferredDeviceId = null;
	}

	/**
	 * Handles device selection from InlineDeviceSelector. Resets all action state for the newly
	 * chosen device and rebuilds sceneData components via `_init_action_components_v2`.
	 */
	function handleDeviceSelect(event: CustomEvent) {
		const deviceRef = event.detail;
		if (!deviceRef) {
			selectedDevice = null;
			return;
		}

		selectedDevice = deviceRef.id || deviceRef;
		selectedChannel = parseInt(String(deviceRef.channel ?? '0'), 10) || 0;

		const device = resolveDeviceInStore(get(devices), selectedDevice, selectedChannel);
		if (!device) return;

		// Rebuild sceneData for new device
		sceneData.selectedDevice = {
			title: device.name || selectedDevice,
			value: selectedDevice,
			id: selectedDevice,
			selected: true,
		};
		const statusKey = real_id(device.id ?? selectedDevice);
		sceneData.deviceAction.device = {
			id: real_id(selectedDevice),
			channel: String(selectedChannel),
			name: device.name || selectedDevice,
		};
		sceneData.deviceAction.actions = [];
		sceneData.deviceAction.delay = { units: 'sec', value: 0 };
		sceneData.deviceAction.timer = { units: 'sec', value: 0 };
		sceneData.do_info = { mode: 'add', type: 'do', main_ind: -1 };

		const status = readDeviceStatus(statusKey);
		const settings = readDeviceSettings(statusKey);

		try {
			sceneData.deviceAction.components = _init_action_components_v2(device, status, settings);
		} catch (e) {
			console.warn('[ActionInspector] Failed to init action components:', e);
			sceneData.deviceAction.components = [];
		}

		// Force reactivity
		sceneData = { ...sceneData };

		node.data.device = {
			id: real_id(selectedDevice),
			name: device.name || selectedDevice,
			channel: String(selectedChannel),
		};
		node.data.actions = [];
		delete node.data.sceneData;
		delete node.data.originalAction;
		node = { ...node, data: { ...node.data } };
		dispatch('update', node);
	}

	/** Handles channel selection changes by re-running `handleDeviceSelect` with the new channel. */
	function handleChannelChange(event: CustomEvent) {
		selectedChannel = event.detail;
		if (selectedDevice) {
			handleDeviceSelect({
				detail: { id: selectedDevice, channel: String(selectedChannel) },
			} as CustomEvent);
		}
	}

	// Get current device object (subscribe so UI updates when devices hydrate)
	$: currentDevice = selectedDevice
		? (resolveDeviceInStore($devices, selectedDevice, selectedChannel) as any)
		: null;
	$: availableChannels = currentDevice?.channels || [0];
	$: hasMultipleChannels = availableChannels.length > 1;
</script>

<!-- Action Type Selector -->
<div class="action-inspector">
	<BaseInspector
		{node}
		title={$format('flow_action_settings', { default: 'Device Action' })}
		on:update
	>
		<Stack gap="s">
			<!-- Device Selection -->
			<InlineDeviceSelector
				selectedDevice={selectedDevice
					? { id: selectedDevice, name: '', channel: String(selectedChannel) }
					: undefined}
				mode="action"
				on:select={handleDeviceSelect}
			/>

			<!-- Channel Selection -->
			{#if selectedDevice && hasMultipleChannels}
				<ChannelSelector
					device={currentDevice}
					channels={availableChannels}
					{selectedChannel}
					on:change={handleChannelChange}
				/>
			{/if}

			<!-- Device Action Components from Scenes -->
			{#if selectedDevice && sceneData.deviceAction.components.length > 0}
				<div class="scene-action-wrapper">
					<ChangeStateStep bind:sceneData />
				</div>
			{/if}

			<!-- Delay before execution -->
			<DelayField
				value={node.data.delay?.value || 0}
				units={node.data.delay?.units || 'sec'}
				on:change={(e) => {
					node.data.delay = e.detail;
					node = { ...node, data: { ...node.data } };
					dispatch('update', node);
				}}
			/>
		</Stack>
	</BaseInspector>
</div>

<style>
	.action-inspector {
		padding: 16px;
		width: 100%;
		min-width: 0;
		max-width: 100%;
		box-sizing: border-box;
		display: flex;
		flex-direction: column;
		gap: 16px;
	}
	.scene-action-wrapper {
		width: 100%;
	}

	.scene-action-wrapper :global(.d-flex) {
		display: flex;
	}

	.scene-action-wrapper :global(.flex-wrap) {
		flex-wrap: wrap;
	}

	.scene-action-wrapper :global(.flex-direction-column) {
		flex-direction: column;
	}

	.scene-action-wrapper :global(.mb-s) {
		margin-bottom: var(--spacing-s, 0.5rem);
	}

	.scene-action-wrapper :global(.mb-m) {
		margin-bottom: var(--spacing-m, 1rem);
	}

	.scene-action-wrapper :global(.mb-l) {
		margin-bottom: var(--spacing-l, 1.5rem);
	}

	.scene-action-wrapper :global(.mt-s) {
		margin-top: var(--spacing-s, 0.5rem);
	}

	.scene-action-wrapper :global(.text-capitalize) {
		text-transform: capitalize;
	}
</style>
